/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useDispatch } from "react-redux";
import { fileViewModes } from "./constants";
import { untranslated } from "@Helpers/types/i18n";
import type { FileSystemReadResponse, FileSystemDeleteResponse } from "./fileSystemServer.schema";

interface UseFileManagerComponentProps {
  fileManagerData?: any;
  setFileManagerData: Function;
  setCurrentFolder?: Function;
  beforeSending?: Function;
  onSuccess?: Function;
  showFolderTree?: boolean;
  path?: string;
  workspaceId?: string;
  onDeleteSuccess?: Function;
  onDeleteError?: Function;
  disableAutoLoad?: boolean;
}

function useFileManagerComponent({
  fileManagerData = { cwd: {}, files: [] },
  setFileManagerData,
  setCurrentFolder = () => {},
  beforeSending = () => {},
  onSuccess = () => {},
  showFolderTree = true,
  path,
  workspaceId,
  onDeleteSuccess = () => {},
  onDeleteError = () => {},
  disableAutoLoad = false,
}: UseFileManagerComponentProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // State
  const [selectedFolder, setSelectedFolder] = useState<any>({ id: "0" });
  const [filesViewMode, setFilesViewMode] = useState(fileViewModes.DETAILED_VIEW);
  const [tempFolder, setTempFolder] = useState<any>({ id: "0" });
  const [refreshGrid, setRefreshGrid] = useState(false);
  const [selectedFilesInIconsMode, setSelectedFilesInIconsMode] = useState<any[]>([]);
  const [isViewRendered, setIsViewRendered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Refs
  const splitterSizeRef = useRef([30, 70]);
  const tempRef = useRef<any>({});
  const treeViewRef = useRef(null);

  // Handlers
  const updateCurrentDir = (directory: any) => {
    setCurrentFolder(directory);
    setSelectedFolder(directory);
    setTempFolder(directory);
  };

  const generatePath = (folder: any) => {
    const innerPath = folder.parentIdArray?.filter((id: string) => id != "0").join("/");
    const prefixPath = innerPath ? `/${innerPath}` : "";
    const suffixPath = folder.id == "0" ? "" : `${selectedFolder.id}/`;
    const finalPath = `${prefixPath}/${suffixPath}`;
    return finalPath;
  };

  const fetchData = async (allIds: any = null, setDirectory = true) => {
    const allParams: any = {
      action: "read",
      path: "/",
    };
    if (allIds) {
      allParams.bulkParentIds = allIds;
    }
    beforeSending(allParams);
    if (!allParams.cancel) {
      let allFiles: any[] = [];
      setRefreshGrid(true);
      const res = await axiosWrapper.post<FileSystemReadResponse>(`${getBackendUrl()}/fileSystemServer/`, {
        ...allParams,
      });
      setIsLoaded(true);
      if (res.data?.files) {
        const directory = { ...res.data.cwd, id: "0" };
        setFileManagerData({
          ...res.data,
          customFieldsByFolder: { "0": (res.data as any)?.customFields ?? [] },
        });
        allFiles = res.data.files;
        if (setDirectory) {
          updateCurrentDir(directory);
        }
        onSuccess({
          result: res.data,
          action: "read",
          path: "/",
        });
      } else if (res.data.errorMessage) {
        if (res.data.cwd) {
          const directory = { ...res.data.cwd, id: "0", isRowDisabled: true };
          setFileManagerData({
            cwd: directory,
            files: [],
          });
        }
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
      setRefreshGrid(false);
      return allFiles;
    }
  };

  const getMoreData = async () => {
    setRefreshGrid(true);
    const payload = { ...selectedFolder };
    const allParams: any = {
      action: "read",
      path: "/",
      data: [payload],
    };
    if (selectedFolder.id != "0") {
      const fetchPath = generatePath(selectedFolder);
      allParams.path = fetchPath;
    }
    beforeSending(allParams);
    if (!allParams.cancel) {
      const res = await axiosWrapper.post<FileSystemReadResponse>(`${getBackendUrl()}/fileSystemServer/`, {
        ...allParams,
      });
      if (res.data?.files) {
        const incomingFiles = res.data?.files || [];
        const incomingFileIds = incomingFiles?.map((file: any) => file.id) || [];
        let freshCurrentFolder = { ...selectedFolder };
        if (res.data?.cwd?._id && res.data?.cwd?._id != "0") {
          incomingFileIds.push(selectedFolder.id);
          freshCurrentFolder = res.data?.cwd;
          incomingFiles.push(freshCurrentFolder);
        }
        let currentFilteredFiles = fileManagerData.files?.filter((file: any) => !incomingFileIds.includes(file.id));
        currentFilteredFiles = currentFilteredFiles.filter((file: any) => file.parentId != selectedFolder.id);
        setFileManagerData((prev: any) => ({
          ...prev,
          files: [...incomingFiles, ...currentFilteredFiles],
          customFieldsByFolder: {
            ...(prev.customFieldsByFolder || {}),
            [freshCurrentFolder.id]: (res.data as any)?.customFields ?? [],
          },
        }));
        onSuccess({
          result: res.data,
          action: "read",
          path: allParams.path,
        });
        setCurrentFolder(freshCurrentFolder);
        setTempFolder(freshCurrentFolder);
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data?.errorMessage || ""),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
      setRefreshGrid(false);
    }
  };

  const fetchMoreData = async () => {
    if (selectedFolder.id !== "0") {
      await getMoreData();
    } else {
      setTempFolder(selectedFolder);
      setCurrentFolder(selectedFolder);
    }
  };

  const refreshFileManager = () => {
    fetchData();
    setSelectedFolder({ id: "0" });
  };

  const refreshFiles = async () => {
    await getMoreData();
  };

  function extractIds() {
    return (path || "")
      .split("/")
      .filter(Boolean)
      .filter((id) => id != "0");
  }

  const fetchBulkData = async () => {
    const allIds: any = extractIds();
    const allFiles: any = await fetchData(allIds, false);
    const lastFolderId: any = allIds[allIds.length - 1];
    const lastFolder = allFiles?.find((file: any) => file.id == lastFolderId);
    if (lastFolder) {
      updateCurrentDir({ ...lastFolder, isBulkFetch: true });
    }
  };

  const handleSearchFiles = async (args: any) => {
    if (args) {
      setRefreshGrid(true);
      const payload = { ...selectedFolder };
      const allParams: any = {
        action: "search",
        caseSensitive: false,
        searchString: `*${args}*`,
        path: "/",
        data: [payload],
      };
      if (selectedFolder.id != "0") {
        const fetchPath = generatePath(selectedFolder);
        allParams.path = fetchPath;
      }
      beforeSending(allParams);
      if (!allParams.cancel) {
        delete allParams.cancel;
        const res = await axiosWrapper.post<FileSystemReadResponse>(`${getBackendUrl()}/fileSystemServer/`, {
          ...allParams,
        });
        if (res.data) {
          setFileManagerData((prev: any) => ({
            ...prev,
            filteredFiles: [...(res.data.files ?? [])],
          }));
        }
        onSuccess({
          result: res.data,
          action: "search",
          path: allParams.path,
        });
        setRefreshGrid(false);
      }
    } else {
      setFileManagerData((prev: any) => ({
        ...prev,
        filteredFiles: null,
      }));
    }
  };

  const getSelectedFilesInFileManager = (filesRef: any) => {
    if (filesViewMode == fileViewModes.DETAILED_VIEW) {
      const selectedFile = filesRef.current.dataView.getAllSelectedItems();
      return selectedFile.filter(Boolean);
    } else if (filesViewMode == fileViewModes.LARGE_ICONS_VIEW) {
      return selectedFilesInIconsMode.filter(Boolean);
    }
    return [];
  };

  const showLoading = () => {
    setRefreshGrid(true);
  };

  const hideLoading = () => {
    setRefreshGrid(false);
  };

  // Delete files/folders
  const deleteFiles = async (filesToDelete: any[], deleteAssociatedTasks = true) => {
    if (!filesToDelete || filesToDelete.length === 0) return { success: false };

    try {
      const selectedFilesToDelete = filesToDelete.map((file: any) => ({
        ...file,
        spaceId: workspaceId,
        deleteAssociatedTasks,
      }));

      const data = {
        action: "delete",
        data: selectedFilesToDelete,
        path: "/",
      };

      beforeSending(data);

      const response = await axiosWrapper.post<FileSystemDeleteResponse>(`${getBackendUrl()}/fileSystemServer`, data);

      if (response.data.status) {
        // Remove deleted files from local state
        const deletedIds = filesToDelete.map((f: any) => f.id);
        setFileManagerData((prev: any) => ({
          ...prev,
          files: prev.files?.filter((f: any) => !deletedIds.includes(f.id)) || [],
        }));

        onDeleteSuccess(filesToDelete);
        dispatch(
          showToastNotification({
            content: filesToDelete.length === 1 ? t("Item deleted successfully") : t("Items deleted successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        return { success: true };
      } else {
        onDeleteError(response.data.message || response.data.errorMessage);
        dispatch(
          showToastNotification({
            content: untranslated(response.data.message || response.data.errorMessage || "Failed to delete"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return { success: false, error: response.data.message };
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : undefined;
      onDeleteError(errMsg || "Failed to delete");
      dispatch(
        showToastNotification({
          content: t("Failed to delete item"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return { success: false, error: errMsg };
    }
  };

  // Check if file can be deleted
  const canDeleteFile = (file: any) => {
    return file?.permission?.FOLDER_DELETE === true;
  };

  // Get current folder files (filtered and sorted)
  const getCurrentFolderFiles = (searchQuery = "") => {
    const data = fileManagerData.filteredFiles || fileManagerData.files || [];
    let items: any[] = [];

    if (tempFolder?.resourceType === "RESOURCE_TYPE_TRANSMITTAL") {
      items = data.filter(
        (item: any) => item.transmittalVersionId === tempFolder.id && item.resourceType !== "RESOURCE_TYPE_TRANSMITTAL",
      );
    } else if (!tempFolder?.resourceType || tempFolder?.id === "0") {
      items = data.filter((item: any) => item.parentId === tempFolder?.id);
    } else {
      items = data.filter((item: any) => item.parentId === tempFolder?.id);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      items = items.filter((item: any) => item.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    // Sort: folders first, then files, alphabetically
    const folders = items.filter(
      (item: any) => item.resourceType === "RESOURCE_TYPE_FOLDER" || item.resourceType === "RESOURCE_TYPE_TRANSMITTAL",
    );
    const files = items.filter(
      (item: any) => item.resourceType !== "RESOURCE_TYPE_FOLDER" && item.resourceType !== "RESOURCE_TYPE_TRANSMITTAL",
    );

    const sortedFolders = [...folders].sort((a: any, b: any) => a.name?.localeCompare(b.name));
    const sortedFiles = [...files].sort((a: any, b: any) => a.name?.localeCompare(b.name));

    return [...sortedFolders, ...sortedFiles];
  };

  // Check if item is a folder
  const isFolder = (item: any) => {
    return item?.resourceType === "RESOURCE_TYPE_FOLDER" || item?.resourceType === "RESOURCE_TYPE_TRANSMITTAL";
  };

  // Check if file can be viewed
  const canViewFile = (file: any) => {
    return file?.permission?.read !== false;
  };

  // Effects
  useEffect(() => {
    let size: any = [];
    if (!showFolderTree) {
      size = [0, 100];
    } else {
      size = [30, 70];
    }
    splitterSizeRef.current = size;
  }, [showFolderTree]);

  useEffect(() => {
    if (disableAutoLoad) return;
    if (path) {
      const allIds = extractIds();
      if (allIds.length) {
        fetchBulkData();
      } else {
        fetchData();
      }
    }
  }, [path]);

  useEffect(() => {
    if (disableAutoLoad) return;
    if (!selectedFolder.isBulkFetch) {
      fetchMoreData();
      setSelectedFilesInIconsMode([]);
      setFileManagerData((prev: any) => ({
        ...prev,
        filteredFiles: null,
      }));
    }
  }, [selectedFolder?.id]);

  return {
    // State
    selectedFolder,
    setSelectedFolder,
    filesViewMode,
    setFilesViewMode,
    tempFolder,
    setTempFolder,
    refreshGrid,
    setRefreshGrid,
    selectedFilesInIconsMode,
    setSelectedFilesInIconsMode,
    isViewRendered,
    setIsViewRendered,
    isLoaded,
    setIsLoaded,

    // Refs
    splitterSizeRef,
    tempRef,
    treeViewRef,

    // Handlers
    updateCurrentDir,
    generatePath,
    fetchData,
    getMoreData,
    fetchMoreData,
    refreshFileManager,
    refreshFiles,
    extractIds,
    fetchBulkData,
    handleSearchFiles,
    getSelectedFilesInFileManager,
    showLoading,
    hideLoading,

    // File operations
    deleteFiles,
    canDeleteFile,
    getCurrentFolderFiles,
    isFolder,
    canViewFile,

    // Props passthrough
    workspaceId,
  };
}

export default useFileManagerComponent;
